<template>
  <div class="towns-map clearfix">
    <div class="towns-map__titles">
      <div class="towns-map__title">年輕族群、都市化程度分層圖</div>
      <div class="towns-map__invited-title">請選擇市鎮類型</div>
    </div>
    <div ref="buttons" class="towns-map__buttons clearfix">
      <div class="towns-map__sub-title">市鎮類型</div>
      <div class="towns-map__buttons-layout">
        <div v-for="(item, index) in townLevelsInfor"
          :key="index"
          @click="onMapButtonClick(index)"
          class="towns-map__button"
          :class="{ 'towns-map__button--active': (currentLevel == index) }"
          :style="{ color: colors[index]}"
          :ref="`button${index}`"
        >
          <div class="towns-map__button--outline" :style="{ 'outline-color': colors[index] }">
            <div class="towns-map__button--mark" :style="{ 'border-left-color': colors[index] }"></div>
            {{ item.name }}
          </div>
        </div>
      </div>
    </div>

    <div class="towns-map__map" ref="map"></div>

    <div class="towns-map__infors">
      <div class="towns-map__infors--label" :style="{backgroundColor: color}">
        {{ levelInfo.name }}
      </div>
      <div class="towns-map__infor">
        <div class="towns-map__infor--label">數量：</div>
        <div class="towns-map__infor--number">{{ levelInfo.amount }} / 368</div>
      </div>
      <div class="towns-map__infor">
        <div class="towns-map__infor--label">平均年輕指數：</div>
        <div class="towns-map__infor--number">{{ levelInfo.ageIndex }}</div>
        <div class="towns-map__infor--rank">
          <SvgSymbol :use="`#award-${levelInfo.ageRank}`" class="towns-map__infor--rank-icon"/>
        </div>
      </div>
      <div class="towns-map__infor">
        <div class="towns-map__infor--label">投票率平均數：</div>
        <div class="towns-map__infor--number">{{ levelInfo.voteProportion | percentage }}</div>
        <div class="towns-map__infor--rank">
          <SvgSymbol :use="`#award-${levelInfo.voteProportionRank}`" class="towns-map__infor--rank-icon"/>
        </div>
      </div>
      <div class="towns-map__infor">
        <div class="towns-map__infor--label">藍營催票率：</div>
        <div class="towns-map__infor--number">{{ levelInfo.dumpupVotesofKMT | percentage }}</div>
      </div>
      <div class="towns-map__infor">
        <div class="towns-map__infor--label">綠營催票率：</div>
        <div class="towns-map__infor--number">{{ levelInfo.dumpupVotesofDPP | percentage }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import * as topojson from 'topojson-client'
import TWTONW from '@/data/tw-town'
import TOWNLEVEL from '@/data/town-level'
import TOWNLEVELSINFOR from '@/data/town-level-infor'

const SVG_CONFIG = {
  viewBox: '0 0 360 560',
  preserveAspectRatio: 'xMidYMid meet',
  width: 360,
  height: 560,
  scaleExtent: [1, 3],
  translateExtent: [[0, 0], [360, 560]]
}

const GEO_CONFIG = {
  center: [123.05, 24.3],
  scale: 5800
}

const COLORS = [
  '#ea6151', '#f18103', '#0192b1', '#03a286', '#8f66a8', '#b8579c', '#d95084'
]

const OUTER_KIMMEN = ['09020010', '09020020', '09020030', '09020040', '09020050', '09020060']
const OUTER_LIANJIANG = ['09007010', '09007020', '09007030', '09007040']

export default {
  name: 'TownsMap',
  data () {
    return {
      geoData: null,
      projection: null,
      geoPath: null,
      svg: null,
      map: null,
      land: null,
      townLevel: 0,
      currentLevel: 0,
      levelsData: [...Array(7)].map(_ => []),
      townLevelsInfor: {},
      levelInfo: {},
      colors: COLORS,
      color: '',
      isButtonActive: false
    }
  },
  props: {},
  computed: {},
  methods: {
    drawLand () {
      let enter = enter => {
        enter
          .append('path')
          .attr('d', data => {
            return this.geoPath(data)
          })
          .attr('class', (d, i) => {
            return 'path' + d.properties.TOWNCODE
          })
          .attr('transform', 'translate(-30, 0)')
          .attr('stroke', '#FFFFFF')
          .attr('stroke-width', '0.3px')
          .style('fill', '#a1a1a1')
          .style('stroke-linejoin', 'round')
          .style('stroke-linecap', 'round')
      }

      let update = update => {
        update
          .attr('d', data => {
            return this.geoPath(data)
          })
      }

      let exit = exit => {
        exit.remove()
      }

      this.land
        .selectAll('path')
        .data(this.geoData.features)
        .join(enter, update, exit)

      this.resetColor()

      OUTER_LIANJIANG.forEach(item => {
        this.land.selectAll(`.path${item}`)
          .attr('transform', 'translate(-10, 70)')
      })

      OUTER_KIMMEN.forEach(item => {
        this.land.selectAll(`.path${item}`)
          .attr('transform', 'translate(20, 0)')
      })
    },
    setColor () {
      this.levelsData[this.townLevel].forEach(item => {
        this.land.selectAll(`.path${item}`)
          .transition()
          .duration(400)
          .style('fill', this.color)
      })
    },
    onMapButtonClick (e) {
      this.townLevel = e
      if (this.townLevel === this.currentLevel) {
        return
      }

      this.resetColor()
      this.color = COLORS[this.townLevel]
      this.currentLevel = this.townLevel
      this.levelInfo = TOWNLEVELSINFOR[this.townLevel]

      this.setColor()
    },
    resetColor () {
      this.land.selectAll('path')
        .transition()
        .duration(400)
        .style('fill', '#a1a1a1')
    }
  },
  mounted () {
    this.geoData = topojson.feature(TWTONW, TWTONW.objects.towns)

    this.projection = d3
      .geoMercator()
      .center(GEO_CONFIG.center)
      .scale(GEO_CONFIG.scale)

    this.geoPath = d3.geoPath().projection(this.projection)

    this.svg = d3
      .select(this.$refs.map)
      .append('svg')
      .attr('viewBox', SVG_CONFIG.viewBox)
      .attr('width', SVG_CONFIG.width)
      .attr('height', SVG_CONFIG.height)

    this.map = this.svg.append('g').classed('map', true)
    this.land = this.map.append('g').classed('land', true)

    TOWNLEVEL.filter(town => {
      this.levelsData[town.level - 1].push(town.id)
    })

    this.townLevelsInfor = TOWNLEVELSINFOR
    this.levelInfo = TOWNLEVELSINFOR[this.townLevel]
    this.color = COLORS[this.townLevel]

    this.drawLand()
    this.setColor()
  }
}
</script>

<style lang="scss">
.towns-map {
  position: relative;
  display: block;
  max-width: 560px;
  margin: 70px auto;
  letter-spacing: 0.1rem;
  overflow: hidden;

  @include rwd($RWD_DESKTOP) {
    max-width: 760px;
  }

  @include rwd($RWD_DESKTOP) {
    max-width: 790px;
  }

  @include rwd($RWD_DESKTOP_WIDE) {
    max-width: 920px;
  }

  &__titles {
    text-align: center;
    margin-bottom: 20px;
    font-weight: 900;
    letter-spacing: 0.1125rem;

    @include rwd($RWD_TABLET) {
      margin-bottom: 55px;
    }

    @include rwd($RWD_DESKTOP) {
      margin-bottom: 65px;
    }
  }

  &__title {
    font-size: 1.1875rem;
    margin-bottom: 20px;
    color: #1a1a1a;

    @include rwd($RWD_TABLET) {
      margin-bottom: 25px;
      font-size: 1.25rem;
    }

    @include rwd($RWD_DESKTOP) {
      font-size: 1.375rem;
      letter-spacing: 0.1375rem;
    }

    @include rwd($RWD_DESKTOP_WIDE) {
      font-size: 1.5rem;
    }
  }

  &__invited-title {
    position: relative;
    display: inline-block;
    color: #a1a1a1;
    padding-bottom: 10px;

    @include rwd($RWD_TABLET) {
      font-size: 1.125rem;
    }
  }

  &__invited-title::before {
    content: "";
    position: absolute;
    left: 5%;
    bottom: 0;
    width: 90%;
    height: 1px;
    background-color: #a1a1a1;
  }

  &__sub-title {
    margin-bottom: 20px;
    text-align: center;
    color: #1a1a1a;
    font-weight: bold;

    @include rwd($RWD_TABLET_SMALL) {
      margin-bottom: 14px;
      font-size: 1.25rem;
      letter-spacing: 0.125rem;
      text-align: left;
    }
  }

  &__map {
    display: block;
    position: relative;
    top: 0;
    right: 0;
    width: 100%;
    height: 0;
    padding-bottom: 140%;
    margin-top: -140px;
    z-index: 1;

    @include rwd($RWD_TABLET_SMALL) {
      position: absolute;
      margin-top: 10px;
      width: 100%;
      padding-bottom: 560 / 480 * 100%;
    }

    @include rwd($RWD_DESKTOP) {
      right: auto;
      width: 85%;
      padding-bottom: 560 * .85 / 480 * .85 * 100%;
    }

    @include rwd($RWD_DESKTOP_WIDE) {
      width: 85%;
      padding-bottom: 560 * .85 / 480 * .85 * 100%;
      left: 20px;
      margin: -30px -80px 0 0;
    }
  }

  svg {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  path.active {
    stroke-linejoin: round;
    stroke-linecap: round;
  }

  &__buttons {
    position: relative;
    padding: 0 5px;
    text-align: center;
    z-index: 5;

    @include rwd($RWD_TABLET_SMALL) {
      padding: 0 12px;
      text-align: left;
    }

    @include rwd($RWD_DESKTOP) {
      float: left;
    }

    @include rwd($RWD_DESKTOP_WIDE) {
      padding: 60px 12px 0 12px;
    }
  }

  &__buttons-layout {
    display: inline-block;
    max-width: 320px;
    text-align: left;
  }

  &__button {
    position: relative;
    display: inline-block;
    width: 50%;
    margin-bottom: 12px;
    text-align: left;
    font-weight: bold;
    outline-style: solid;
    outline-width: 0;
    outline-color: initial;
    cursor: pointer;
    user-select: none;

    @include rwd($RWD_TABLET_SMALL) {
      display: block;
      float: left;
      clear: both;
      width: auto;
      margin-bottom: 10px;
    }
  }

  &__button--outline {
    position: relative;
    display: inline-block;
    padding: 1px 5px;
    height: 100%;

    @include rwd($RWD_DESKTOP) {
      padding: 3px 10px;
    }
  }

  &__button--active &__button--outline,
  &__button--outline:hover {
    outline-color: inherit;
    outline-width: 1px;
    outline-style: solid;
  }

  &__button--mark {
    display: inline-block;
    padding-right: 3px;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 8px solid transparent;
  }

  &__infors {
    position: relative;
    margin-top: -30px;
    padding: 0 40px;
    z-index: 5;

    @include rwd($RWD_TABLET_SMALL) {
      margin-top: 45px;
      padding: 0 12px;
    }

    @include rwd($RWD_DESKTOP) {
      width: 280px;
      float: right;
      margin-top: 0;
      padding: 170px 0 100px 30px;
    }

    @include rwd($RWD_DESKTOP_WIDE) {
      padding: 60px 0 280px 20px;
    }
  }

  &__infors--label {
    display: inline-block;
    padding: 2px 8px;
    margin-bottom: 16px;
    color: #eeeeee;
    font-weight: bold;

    @include rwd($RWD_TABLET) {
      margin-bottom: 26px;
      padding: 2px 15px;
      font-size: 1.25rem;
    }

    @include rwd($RWD_DESKTOP) {
      letter-spacing: 0.1125rem;
      margin-bottom: 16px;
    }

    @include rwd($RWD_DESKTOP_WIDE) {
    }
  }

  &__infor {
    margin-bottom: 10px;
  }

  &__infor::before {
    content: "";
    display: inline-block;
    width: 7px;
    height: 7px;
    margin: 0 8px 0 2px;
    background-color: #000;
    transform: rotate(45deg);
    vertical-align: middle;
  }

  &__infor--label {
    display: inline-block;
    color: #1a1a1a;
    font-weight: 600;
    font-size: 0.875rem;
    letter-spacing: 0.0875rem;

    @include rwd($RWD_TABLET) {
      font-size: 1rem;
    }
  }

  &__infor--number {
    display: inline-block;
    color: #1a1a1a;
    font-weight: bold;
    font-size: 0.875rem;

    @include rwd($RWD_TABLET) {
      font-size: 1rem;
    }
  }

  &__infor--rank {
    position: relative;
    display: inline-block;
    width: 25px;
    height: 23px;
    margin-left: 5px;
    color: #fff;
    vertical-align: text-bottom;

    @include rwd($RWD_TABLET_SMALL) {
      margin-left: 8px;
    }
  }
}
</style>
